import React, { createContext, useContext, useState, useEffect } from 'react';
import apiService from '../services/api';
import toast from 'react-hot-toast';

const DatabaseContext = createContext();

export function DatabaseProvider({ children }) {
  const [customers, setCustomers] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [useDatabase, setUseDatabase] = useState(false);

  // Check if database is available on mount
  useEffect(() => {
    checkDatabaseConnection();
  }, []);

  const checkDatabaseConnection = async () => {
    try {
      const connected = await apiService.testConnection();
      setUseDatabase(connected);
      if (connected) {
        loadCustomers();
        loadTransactions();
        toast.success('Database connected successfully!');
      } else {
        // Fallback to localStorage
        loadLocalData();
        toast.error('Database not available, using local storage');
      }
    } catch (err) {
      console.error('Database connection failed:', err);
      setUseDatabase(false);
      loadLocalData();
      toast.error('Database connection failed, using local storage');
    }
  };

  const loadLocalData = () => {
    const savedCustomers = localStorage.getItem('customers');
    const savedTransactions = localStorage.getItem('transactions');
    
    if (savedCustomers) {
      setCustomers(JSON.parse(savedCustomers));
    }
    if (savedTransactions) {
      setTransactions(JSON.parse(savedTransactions));
    }
  };

  const saveToLocalStorage = (key, data) => {
    if (!useDatabase) {
      localStorage.setItem(key, JSON.stringify(data));
    }
  };

  // Customer operations
  const loadCustomers = async () => {
    if (!useDatabase) return;
    
    setLoading(true);
    try {
      const response = await apiService.getCustomers();
      if (response.status === 'success') {
        setCustomers(response.data);
      }
    } catch (err) {
      setError(err.message);
      toast.error('Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  const addCustomer = async (customerData) => {
    setLoading(true);
    try {
      if (useDatabase) {
        const response = await apiService.createCustomer(customerData);
        if (response.status === 'success') {
          await loadCustomers(); // Reload to get the customer with ID
          toast.success('Customer added successfully!');
          return response;
        } else {
          throw new Error(response.message);
        }
      } else {
        // Local storage fallback
        const newCustomer = { ...customerData, id: Date.now() };
        const updatedCustomers = [...customers, newCustomer];
        setCustomers(updatedCustomers);
        saveToLocalStorage('customers', updatedCustomers);
        toast.success('Customer added locally!');
        return { status: 'success', data: { id: newCustomer.id } };
      }
    } catch (err) {
      setError(err.message);
      toast.error('Failed to add customer: ' + err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateCustomer = async (id, customerData) => {
    setLoading(true);
    try {
      if (useDatabase) {
        const response = await apiService.updateCustomer(id, customerData);
        if (response.status === 'success') {
          await loadCustomers();
          toast.success('Customer updated successfully!');
          return response;
        } else {
          throw new Error(response.message);
        }
      } else {
        // Local storage fallback
        const updatedCustomers = customers.map(customer =>
          customer.id === id ? { ...customerData, id } : customer
        );
        setCustomers(updatedCustomers);
        saveToLocalStorage('customers', updatedCustomers);
        toast.success('Customer updated locally!');
        return { status: 'success' };
      }
    } catch (err) {
      setError(err.message);
      toast.error('Failed to update customer: ' + err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const deleteCustomer = async (id) => {
    setLoading(true);
    try {
      if (useDatabase) {
        const response = await apiService.deleteCustomer(id);
        if (response.status === 'success') {
          await loadCustomers();
          toast.success('Customer deleted successfully!');
          return response;
        } else {
          throw new Error(response.message);
        }
      } else {
        // Local storage fallback
        const updatedCustomers = customers.filter(customer => customer.id !== id);
        setCustomers(updatedCustomers);
        saveToLocalStorage('customers', updatedCustomers);
        toast.success('Customer deleted locally!');
        return { status: 'success' };
      }
    } catch (err) {
      setError(err.message);
      toast.error('Failed to delete customer: ' + err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Transaction operations
  const loadTransactions = async () => {
    if (!useDatabase) return;
    
    setLoading(true);
    try {
      const response = await apiService.getTransactions();
      if (response.status === 'success') {
        setTransactions(response.data);
      }
    } catch (err) {
      setError(err.message);
      toast.error('Failed to load transactions');
    } finally {
      setLoading(false);
    }
  };

  const addTransaction = async (transactionData) => {
    setLoading(true);
    try {
      if (useDatabase) {
        const response = await apiService.createTransaction(transactionData);
        if (response.status === 'success') {
          await loadTransactions();
          toast.success('Transaction added successfully!');
          return response;
        } else {
          throw new Error(response.message);
        }
      } else {
        // Local storage fallback
        const newTransaction = { 
          ...transactionData, 
          id: Date.now(), 
          created_at: new Date().toISOString() 
        };
        const updatedTransactions = [...transactions, newTransaction];
        setTransactions(updatedTransactions);
        saveToLocalStorage('transactions', updatedTransactions);
        toast.success('Transaction added locally!');
        return { status: 'success', data: { id: newTransaction.id } };
      }
    } catch (err) {
      setError(err.message);
      toast.error('Failed to add transaction: ' + err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateTransaction = async (id, transactionData) => {
    setLoading(true);
    try {
      if (useDatabase) {
        const response = await apiService.updateTransaction(id, transactionData);
        if (response.status === 'success') {
          await loadTransactions();
          toast.success('Transaction updated successfully!');
          return response;
        } else {
          throw new Error(response.message);
        }
      } else {
        // Local storage fallback
        const updatedTransactions = transactions.map(transaction =>
          transaction.id === id ? { ...transactionData, id } : transaction
        );
        setTransactions(updatedTransactions);
        saveToLocalStorage('transactions', updatedTransactions);
        toast.success('Transaction updated locally!');
        return { status: 'success' };
      }
    } catch (err) {
      setError(err.message);
      toast.error('Failed to update transaction: ' + err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const deleteTransaction = async (id) => {
    setLoading(true);
    try {
      if (useDatabase) {
        const response = await apiService.deleteTransaction(id);
        if (response.status === 'success') {
          await loadTransactions();
          toast.success('Transaction deleted successfully!');
          return response;
        } else {
          throw new Error(response.message);
        }
      } else {
        // Local storage fallback
        const updatedTransactions = transactions.filter(transaction => transaction.id !== id);
        setTransactions(updatedTransactions);
        saveToLocalStorage('transactions', updatedTransactions);
        toast.success('Transaction deleted locally!');
        return { status: 'success' };
      }
    } catch (err) {
      setError(err.message);
      toast.error('Failed to delete transaction: ' + err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const value = {
    // Data
    customers,
    transactions,
    loading,
    error,
    useDatabase,
    
    // Customer operations
    loadCustomers,
    addCustomer,
    updateCustomer,
    deleteCustomer,
    
    // Transaction operations
    loadTransactions,
    addTransaction,
    updateTransaction,
    deleteTransaction,
    
    // Utility
    checkDatabaseConnection
  };

  return (
    <DatabaseContext.Provider value={value}>
      {children}
    </DatabaseContext.Provider>
  );
}

export function useDatabase() {
  const context = useContext(DatabaseContext);
  if (!context) {
    throw new Error('useDatabase must be used within a DatabaseProvider');
  }
  return context;
}

export default DatabaseContext;
